import {useState} from 'react';
import PropTypes from 'prop-types';
import {Translate} from 'react-redux-i18n';
import {useDispatch, useSelector} from 'react-redux';

import * as snackbarsActions from '../../actions/snackbars';
import relationshipService from '../../services/relationship/RelationshipService';
import {errorCode, status} from '../../utils/constant';
import enrollmentService from '../../services/enrollment';
import SubcontractorAddView from './SubcontractorAddView';
import {captureError} from '../../utils/log';

import ExternalInvitationSuccess from '../externalInvitationSuccess';

function SubcontractorAddContainer({onClose, reinvitedOrganisation}) {
  const {organisationId} = useSelector(state => state.organisation);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  function handleSelectInternalInvitation(subcontractor) {
    return relationshipService.searchForSubcontractor(subcontractor.organisationId, organisationId)
      .then(response => {
        if (subcontractor.depositor && response && response[0]) {
          const responseStatus = response[0].status;
          const businessError = new Error();
          businessError.name = 'businessError';

          if (responseStatus === status.ACCEPTED) {
            businessError.message = 'organisation.search.alreadySubcontractor';
            return Promise.reject(businessError);
          }

          if (responseStatus === status.PENDING) {
            businessError.message = 'organisation.search.alreadyInvited';
            return Promise.reject(businessError);
          }

          if ([status.REJECTED, status.CANCELLED].includes(responseStatus)) {
            return Promise.resolve();
          }

          businessError.message = 'error.generic';
          return Promise.reject(businessError);
        }

        return Promise.resolve();
      })
      .then(() => {
        if (subcontractor.depositor) {
          relationshipService.addSubcontractors([subcontractor.organisationId], organisationId)
            .then(([response]) => {
              const isAccepted = response?.status === status.ACCEPTED;
              dispatch(snackbarsActions.addSuccess(<Translate
                value={isAccepted ?
                  'subcontractor.add.addSuccess' :
                  'subcontractor.add.inviteSuccess'
                }
                name={subcontractor.fullName}
              />));
            })
            .catch(error => {
              dispatch(snackbarsActions.addError(<Translate value="error.generic"/>));
              captureError('Error while adding subcontractor', error);
            })
            .finally(onClose);
        }
      })
      .catch(error => {
        if (error.name === 'businessError') {
          return Promise.reject(error);
        }

        captureError('Error while searching for subcontractor', error);
        dispatch(snackbarsActions.addError(<Translate value="error.generic"/>));
        return Promise.reject(new Error('error.generic'));
      });
  }

  function handleExternalInvitation(invitation) {
    const invit = {...invitation, invitingClientId: organisationId};
    return enrollmentService.network.inviteSubcontractors(organisationId, [invit])
      .then(() => { setIsModalOpen(true); })
      .catch(async error => {
        const errorObject = await error.response.json();

        if (errorObject?.errorCode === errorCode.SAME_INVITATION_ALREADY_PENDING) {
          return Promise.resolve(errorCode.SAME_INVITATION_ALREADY_PENDING);
        }

        captureError('Error while adding client invitations', error);
        dispatch(snackbarsActions.addError(<Translate value="error.generic"/>));
        onClose();
        return Promise.resolve();
      });
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    onClose();
  }

  return (
    <>
      <SubcontractorAddView
        onClose={onClose}
        onSelectInternalInvitation={handleSelectInternalInvitation}
        onExternalInvitation={handleExternalInvitation}
        reinvitedOrganisation={reinvitedOrganisation}
      />
      <ExternalInvitationSuccess
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        text={<Translate value="subcontractor.invite.inviteSuccess"/>}
      />
    </>
  );
}

SubcontractorAddContainer.propTypes = {
  onClose: PropTypes.func.isRequired,
  reinvitedOrganisation: PropTypes.object
};

export default SubcontractorAddContainer;
